import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { ClientEnvironment } from '@polyai/common/constants/global.constants';

import api from 'api';
import type { HTTPError } from 'api/error';
import type {
  CreateProjectFromTemplateReq,
  ProjectRes,
  ProjectsRes,
} from 'api/resources/projects/types';
import { curryPostFunction } from 'api/utils';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import { useToast } from 'hooks/useToast';
import { useParagonOperation } from './useParagon';
import { PROJECT_MUTATION_KEY } from './useProject';
import useProjectRoute from './useProjectRoute';
import { generateProjectsQueryKey } from './useProjects';

const useProjectMutations = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const isPlgProd = useIsPlgProd();
  const { disconnectAllIntegrations } = useParagonOperation();

  const { accountId, projectId } = useProjectRoute();

  const projectsQueryKey = generateProjectsQueryKey(accountId);
  const projectQueryKey = [accountId, projectId, PROJECT_MUTATION_KEY];

  const { mutate: deleteProject, isLoading: isDeletingProject } = useMutation(
    curryPostFunction(api.deleteProject)({ accountId }),
    {
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries(projectsQueryKey);
        void disconnectAllIntegrations();
        if (isPlgProd) {
          api
            .deleteWebRTCConnection({
              accountId,
              projectId: variables.projectId,
              clientEnv: ClientEnvironment.LIVE,
            })
            .catch(err => {
              console.error('Failed to delete WebRTC connection', {
                accountId,
                projectId: variables.projectId,
                err,
              });
            });
        }
        toast.success({
          title: 'Agent deleted',
        });
      },
      onError: () => {
        void queryClient.invalidateQueries({
          queryKey: projectsQueryKey,
        });

        toast.error({
          title: 'Delete agent failed',
        });
      },
    },
  );

  const {
    mutate: duplicateProject,
    mutateAsync: duplicateProjectAsync,
    isLoading: isDuplicatingProject,
  } = useMutation(
    async ({
      projectId,
      newProjectId,
      newProjectName,
    }: {
      projectId: string;
      newProjectId?: string;
      newProjectName?: string;
    }) => {
      interface DuplicatePayload {
        projectId: string;
        newProjectId: string | undefined;
        newProjectName: string | undefined;
      }
      const payload: DuplicatePayload = {
        projectId,
        newProjectId: newProjectId || undefined,
        newProjectName: newProjectName || undefined,
      };
      return curryPostFunction(api.duplicateProject)({ accountId })(payload);
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries({
          queryKey: projectsQueryKey,
        });
        const previousProjects =
          queryClient.getQueryData<ProjectsRes>(projectsQueryKey);

        if (!previousProjects) {
          return;
        }

        const newProjects: ProjectsRes = {
          projects: [
            { id: uuidv4(), isDummy: true },
            ...previousProjects.projects,
          ],
        };

        queryClient.setQueryData(projectsQueryKey, newProjects);

        return { previousProjects: previousProjects };
      },
      onSuccess: () => {
        void queryClient.invalidateQueries(projectsQueryKey);
        toast.success({
          title: 'Agent duplicated',
        });
      },
      onError: (error, __, context) => {
        if (!context) {
          void queryClient.invalidateQueries({
            queryKey: projectsQueryKey,
          });
        } else {
          queryClient.setQueryData(projectsQueryKey, context.previousProjects);
        }

        // Check for specific error codes
        if (error && typeof error === 'object' && 'code' in error) {
          switch (error.code) {
            case 'ProjectIdAlreadyExists':
              toast.error({
                title: 'Agent duplication failed',
                description:
                  'Agent with provided ID already exists, please try a different ID',
              });
              break;
            default:
              toast.error({
                title: 'Agent duplication failed',
              });
          }
        } else {
          toast.error({
            title: 'Agent duplication failed',
          });
        }
      },
    },
  );

  const { mutate: importProject, isLoading: isImportingProject } = useMutation(
    async ({
      file,
      newProjectId,
      newProjectName,
    }: {
      file: File;
      newProjectId?: string;
      newProjectName?: string;
    }) => {
      return api.importProject({
        accountId,
        file,
        newProjectId,
        newProjectName,
      });
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries({
          queryKey: projectsQueryKey,
        });
        const previousProjects =
          queryClient.getQueryData<ProjectsRes>(projectsQueryKey);

        if (!previousProjects) {
          return;
        }

        const newProjects: ProjectsRes = {
          projects: [
            { id: uuidv4(), isDummy: true },
            ...previousProjects.projects,
          ],
        };

        queryClient.setQueryData(projectsQueryKey, newProjects);

        return { previousProjects };
      },
      onSuccess: () => {
        void queryClient.invalidateQueries(projectsQueryKey);
        toast.success({
          title: 'Agent imported',
        });
      },
      onError: (error, __, context) => {
        if (!context) {
          void queryClient.invalidateQueries({
            queryKey: projectsQueryKey,
          });
        } else {
          queryClient.setQueryData(projectsQueryKey, context.previousProjects);
        }

        // Check for specific error codes
        if (error && typeof error === 'object' && 'code' in error) {
          switch (error.code) {
            case 'ProjectIdAlreadyExists':
              toast.error({
                title: 'Agent import failed',
                description:
                  'Agent with provided ID already exists, please try a different ID',
              });
              break;
            default:
              toast.error({
                title: 'An error occurred while importing. Try again later.',
              });
          }
        } else {
          toast.error({
            title: 'An error occurred while importing. Try again later.',
          });
        }
      },
      onSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: projectsQueryKey,
        });
      },
    },
  );
  const { mutate: updateProject, isLoading: isUpdatingProject } = useMutation(
    curryPostFunction(api.updateProject)({ accountId, projectId }),
    {
      mutationKey: projectQueryKey,
      onSuccess: () => {
        void queryClient.invalidateQueries(projectsQueryKey);
        void queryClient.invalidateQueries(projectQueryKey);
        toast.success({
          title: 'Agent updated',
        });
      },
      onError: () => {
        toast.error({
          title: 'Agent update failed',
        });
      },
    },
  );

  const { mutate: createProject, isLoading: isCreatingProject } = useMutation(
    curryPostFunction(api.createProject)({
      accountId,
    }),
    {
      onSuccess: () => {
        void queryClient.resetQueries(projectsQueryKey);
      },
    },
  );

  const {
    mutate: createProjectFromTemplate,
    isLoading: isCreatingProjectFromTemplate,
  } = useMutation<ProjectRes, HTTPError, CreateProjectFromTemplateReq>(
    body =>
      api.createProjectFromTemplate({
        accountId,
        body,
      }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(generateProjectsQueryKey(accountId));
      },
      onError: () => {
        toast.error({
          title: 'Failed to create project from template',
          description:
            'Please try again. If the problem persists, contact support.',
        });
      },
    },
  );

  return {
    accountId,
    projectId,
    deleteProject,
    isDeletingProject,
    duplicateProject,
    duplicateProjectAsync,
    isDuplicatingProject,
    importProject,
    isImportingProject,
    updateProject,
    isUpdatingProject,
    createProject,
    isCreatingProject,
    createProjectFromTemplate,
    isCreatingProjectFromTemplate,
  };
};

export default useProjectMutations;
